/** @jsx jsx */

import { ComponentProps } from 'react';

import { getGravatarUrl } from '@zapier/common-utils';
import {
  Avatar,
  Icon,
  TableRow,
  TableTd,
  Tag,
  Text,
  TooltipWrapper,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import Link from 'app/common/components/Link';
import {
  INVITE_STATUSES,
  MEMBER_CONTACT_TYPES,
  ROLES,
} from 'app/developer-v3/constants';

import { tooltipsContent } from './constants';

import type { Member as MemberType } from 'app/developer-v3/types/member';

const Styles = {
  infoWrapper: css`
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  tooltipWrapper: css`
    min-width: 200px;
    text-align: center;
  `,
  tagLabelContents: css`
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
};

type ActionItems = ComponentProps<typeof ActionsDropdownMenu>['items'];
type ContactType = MemberType['contactTypes'][0];

type Props = {
  hasMultipleAcceptedAdmins: boolean;
  isNotIntegrationAdmin: boolean;
  member: MemberType;
  onDelete: (member: MemberType) => void;
  onSetAdminContactType: (contactType: ContactType) => Promise<void>;
};

function Member({
  hasMultipleAcceptedAdmins,
  isNotIntegrationAdmin,
  member,
  onDelete,
  onSetAdminContactType,
}: Props) {
  const { contactTypes = [], email, gravatar, name, role, status } = member;

  const isAdmin = role === ROLES.admin;
  const isPending = status !== INVITE_STATUSES.accepted;
  const isMarketingContact = contactTypes.includes(
    MEMBER_CONTACT_TYPES.marketing
  );
  const isTechnicalContact = contactTypes.includes(
    MEMBER_CONTACT_TYPES.technical
  );
  const canBeDeleted =
    (hasMultipleAcceptedAdmins || // there are multiple accepted admins
    isPending || // the current member row is pending
      !isAdmin) && // the current member row is not an admin.
    !isMarketingContact && // the current member is not a marketing contact
    !isTechnicalContact; // the current member is not a technical contact
  const canMarketingContactBeSet = isAdmin && !isPending && !isMarketingContact;
  const canTechnicalContactBeSet = isAdmin && !isPending && !isTechnicalContact;

  const adminActionItems: ActionItems = [];

  // TODO: show these options always but make them disabled instead of hiding
  // them. Use something like a tooltip to explain why they are disabled.
  if (canBeDeleted) {
    adminActionItems.push({
      isConfirmationRequired: true,
      label: 'Remove team member',
      modalTextContent: `${
        name || email
      } will not have access to manage this integration.`,
      modalTitle: 'Remove team member',
      onChoose: () => onDelete(member),
      value: 'delete',
    });
  }
  if (canMarketingContactBeSet) {
    adminActionItems.push({
      isConfirmationRequired: true,
      label: 'Set marketing contact',
      modalTextContent: `${
        name || email
      } will become the marketing contact. We'll reach out to this person for marketing announcements.`,
      modalTitle: 'Set marketing contact',
      onChoose: () => onSetAdminContactType(MEMBER_CONTACT_TYPES.marketing),
      value: 'set-marketing-contact',
    });
  }
  if (canTechnicalContactBeSet) {
    adminActionItems.push({
      label: 'Set technical contact',
      isConfirmationRequired: true,
      modalTextContent: `${
        name || email
      } will become the technical contact. We'll reach out to this person for technical support. E.g., our support team might contact this person so we can collaborate on trickier Customer Support tickets on Zaps that involve your integration.`,
      modalTitle: 'Set technical contact',
      onChoose: () => onSetAdminContactType(MEMBER_CONTACT_TYPES.technical),
      value: 'set-technical-contact',
    });
  }

  // Show admin actions to admins only, nothing to collaborators.
  const actionItemsToShow = isNotIntegrationAdmin ? [] : adminActionItems;

  return (
    <TableRow>
      <TableTd>
        <Avatar
          color="primary"
          name={name || email}
          size="medium"
          url={getGravatarUrl(gravatar)}
        />
      </TableTd>
      <TableTd>
        <div>
          {email !== name && !!name && (
            <Text fontWeight={600} tag="p" type="Body4">
              {name}
            </Text>
          )}
          <Text>
            <Link href={`mailto:${email}`}>{email}</Link>
          </Text>
        </div>
      </TableTd>
      <TableTd>
        <TooltipWrapper
          allowMultilineTooltip={true}
          content={
            <div css={Styles.tooltipWrapper}>{tooltipsContent[role]}</div>
          }
          isBlock={true}
        >
          {() =>
            role === ROLES.admin ? (
              <Tag variant="StatusSuccessStrongest">
                <span css={Styles.tagLabelContents}>Admin</span>
              </Tag>
            ) : role === ROLES.subscriber ? (
              <Tag variant="success">
                <span css={Styles.tagLabelContents}>Subscriber (Legacy)</span>
              </Tag>
            ) : role === ROLES.collaborator ? (
              <Tag variant="success">
                <span css={Styles.tagLabelContents}>Collaborator</span>
              </Tag>
            ) : null
          }
        </TooltipWrapper>
      </TableTd>
      <TableTd>
        {isPending ? (
          <div css={Styles.infoWrapper}>
            <Icon name="miscClockFill" size={16} />
            <Text>Invitation Pending</Text>
          </div>
        ) : (
          <div css={Styles.infoWrapper}>
            {isMarketingContact && (
              <Tag variant="inverted">
                <span css={Styles.tagLabelContents}>Marketing contact</span>
              </Tag>
            )}
            {isTechnicalContact && (
              <Tag variant="inverted">
                <span css={Styles.tagLabelContents}>Technical contact</span>
              </Tag>
            )}
          </div>
        )}
      </TableTd>
      <TableTd>
        {actionItemsToShow.length > 0 ? (
          <ActionsDropdownMenu items={actionItemsToShow} />
        ) : null}
      </TableTd>
    </TableRow>
  );
}

export default Member;
